import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import {
  BehaviorSubject,
  combineLatest,
  EMPTY,
  Observable,
  Subject,
  Subscription
} from 'rxjs';
import { catchError, distinctUntilChanged, startWith, switchMap, takeUntil } from 'rxjs/operators';
import { AuthService } from '../../../../core/services/auth/auth.service';
import { TopicsApi } from '../../apis/topics.api';
import { IChatInputMessage } from '../../interfaces/chat-input.interface';
import { IConversationItem } from '../../interfaces/conversation-history.interface';
import { ISelectedInformation } from '../../interfaces/selected-information.interface';
import {
  EnumContexto,
  EnumTopicType,
  ITopic,
  ITopicsResponse
} from '../../interfaces/topic.interface';
import { AyaService } from '../../services/aya.service';
import { ChatHistoryStateService } from '../../services/chat-history-state.service';
import { ChatLauncherService } from '../../services/chat-launcher.service';
import { PreferencesModalService } from '../../services/preferences-modal.service';
import { ChatPanelService } from '../../services/chat-panel.service';
import { buildGreeting } from '../../utils/build-greeting.util';
import { resolveContextoFromRoute } from '../../utils/contexto-from-route.util';
import { ChatConversationComponent } from '../chat-conversation/chat-conversation.component';

@Component({
  selector: 'sgt-chat-panel',
  templateUrl: './chat-panel.component.html',
  styleUrls: ['./chat-panel.component.scss']
})
export class ChatPanelComponent implements OnInit, OnDestroy {
  isOpen = false;
  isHistoryOpen = false;
  conversations: IConversationItem[] = [];
  isAgentTyping = false;
  loadingHistory = false;
  title: string | null = null;
  sessionId: string | null = null;
  greeting = '';
  topics: ITopic[] = [];
  // true até o primeiro ciclo de abertura do painel, para evitar flash do fallback
  loadingTopics = true;
  topicsError = false;
  information: ISelectedInformation | null = null;

  @ViewChild(ChatConversationComponent)
  conversation?: ChatConversationComponent;

  private contextoAtual?: EnumContexto;
  private destroy$ = new Subject<void>();
  private readonly topicsRetry$ = new Subject<void>();
  private readonly sessionId$ = new BehaviorSubject<string | null>(null);
  creatingSession = false;
  pendingMessage: IChatInputMessage | null = null;
  creationFailed = false;
  private launcherSubscription: Subscription | null = null;
  private historySubscription: Subscription | null = null;

  constructor(
    private router: Router,
    private ayaService: AyaService,
    private chatPanelService: ChatPanelService,
    private chatLauncher: ChatLauncherService,
    private historyState: ChatHistoryStateService,
    private topicsApi: TopicsApi,
    private authService: AuthService,
    private preferencesModalService: PreferencesModalService
  ) {}

  openPreferences(): void {
    this.preferencesModalService.open();
  }

  ngOnInit(): void {
    this.greeting = buildGreeting(this.authService.getUsuario()?.nome);
    this.subscribeToPanelState();
    this.subscribeToTypingState();
    this.subscribeToTitle();
    this.subscribeToConversations();
    this.ayaService.removedSession$
      .pipe(takeUntil(this.destroy$))
      .subscribe((id) => {
        if (id === null || id === this.sessionId) {
          this.discardCurrent();
          this.selectSession(null);
          this.closeHistory();
        }
      });
  }

  onMessageSent(msg: IChatInputMessage): void {
    if (this.creatingSession || this.loadingHistory) {
      return;
    }
    const message = this.information ? { ...msg, selectedInformation: this.information } : msg;
    if (this.sessionId) {
      this.ayaService.sendMessage(this.sessionId, message);
      return;
    }

    this.pendingMessage = message;
    this.creationFailed = false;
    this.creatingSession = true;
    this.launcherSubscription = this.chatLauncher
      .start(this.contextoAtual)
      .subscribe({
        next: ({ id }) => {
          this.creatingSession = false;
          this.selectSession(id);
          this.pendingMessage = null;
          this.ayaService.sendMessage(id, message);
        },
        error: () => {
          this.creatingSession = false;
          this.creationFailed = true;
        }
      });
  }

  retryPendingMessage(): void {
    if (this.pendingMessage) {
      this.onMessageSent(this.pendingMessage);
    }
  }

  onTopicSelected(topic: ITopic): void {
    if (topic.type === EnumTopicType.QuickAction && topic.quickActionId) {
      this.onMessageSent({
        text: '',
        quickActionId: topic.quickActionId,
        quickActionTitle: topic.message
      });
    } else {
      this.onMessageSent({ text: topic.message });
    }
  }

  onInformationRemoved(): void {
    this.chatPanelService.clearSelectedInformation();
  }

  retryTopics(): void {
    this.topicsRetry$.next();
  }

  onClose(): void {
    this.resetPanel();
  }

  openInFullPage(): void {
    const id = this.sessionId;
    this.resetPanel();
    this.router.navigate(id ? ['/ceppem-ai/chat', id] : ['/ceppem-ai/chat']);
  }

  toggleHistory(): void {
    this.isHistoryOpen = !this.isHistoryOpen;
    if (this.isHistoryOpen) {
      this.historyState.load();
    }
  }

  closeHistory(): void {
    this.isHistoryOpen = false;
  }

  startNewChat(): void {
    this.closeHistory();
    this.discardCurrent();
    this.selectSession(null);
    this.loadingHistory = false;
    this.topics = [];
    this.clearInformation();
    this.focusChatInput();
  }

  selectConversation(item: IConversationItem): void {
    this.closeHistory();
    if (item.id === this.sessionId) {
      return;
    }

    this.discardCurrent();
    this.clearInformation();
    this.selectSession(item.id);
    this.loadingHistory = true;
    this.historySubscription = this.ayaService
      .openConversation(item.id)
      .subscribe({
        next: () => {
          this.loadingHistory = false;
        },
        error: () => {
          this.loadingHistory = false;
          this.ayaService.showAgentError(
            'Não foi possível carregar a conversa selecionada.'
          );
        }
      });
  }

  trackBySessionId(_: number, item: IConversationItem): string {
    return item.id;
  }

  ngOnDestroy(): void {
    this.discardCurrent();
    this.ayaService.setVisible('panel', null);
    this.destroy$.next();
    this.destroy$.complete();
  }

  private selectSession(id: string | null): void {
    this.sessionId = id;
    this.sessionId$.next(id);
    this.ayaService.setVisible('panel', this.isOpen ? id : null);
  }

  private subscribeToPanelState(): void {
    combineLatest([
      this.chatPanelService.getIsOpen$(),
      this.chatPanelService.getSelectedInformation$(),
      this.topicsRetry$.pipe(startWith(undefined))
    ])
      .pipe(
        takeUntil(this.destroy$),
        switchMap(([isOpen, information]) => this.onPanelStateChange(isOpen, information))
      )
      .subscribe((response) => {
        this.topics = response.topics ?? [];
        this.loadingTopics = false;
      });
  }

  private clearInformation(): void {
    if (this.information) {
      this.chatPanelService.clearSelectedInformation();
    }
  }

  private onPanelStateChange(
    isOpen: boolean,
    information: ISelectedInformation | null
  ): Observable<ITopicsResponse> {
    this.isOpen = isOpen;
    this.ayaService.setVisible('panel', isOpen ? this.sessionId : null);
    this.information = information;
    this.topics = [];
    this.topicsError = false;

    if (!isOpen) {
      this.loadingTopics = true;
      this.closeHistory();
      return EMPTY;
    }

    this.focusChatInput();
    this.contextoAtual = resolveContextoFromRoute(
      this.router.routerState.snapshot.root
    );
    if (information) {
      this.loadingTopics = true;
      return this.topicsApi.fetchTopicsByInformation([information.id]).pipe(
        catchError(() => {
          this.topicsError = true;
          this.loadingTopics = false;
          return EMPTY;
        })
      );
    }

    if (!this.contextoAtual) {
      this.loadingTopics = false;
      return EMPTY;
    }

    this.loadingTopics = true;
    return this.topicsApi.fetchTopics(this.contextoAtual).pipe(
      catchError(() => {
        this.topics = [];
        this.loadingTopics = false;
        return EMPTY;
      })
    );
  }

  private subscribeToTypingState(): void {
    this.sessionId$
      .pipe(
        distinctUntilChanged(),
        switchMap((id) => this.ayaService.getIsTyping$(id)),
        takeUntil(this.destroy$)
      )
      .subscribe((isTyping) => (this.isAgentTyping = isTyping));
  }

  private subscribeToTitle(): void {
    this.sessionId$
      .pipe(
        distinctUntilChanged(),
        switchMap((id) => this.ayaService.getTitle$(id)),
        takeUntil(this.destroy$)
      )
      .subscribe((title) => (this.title = title));
  }

  private subscribeToConversations(): void {
    this.historyState.itemsObservable
      .pipe(takeUntil(this.destroy$))
      .subscribe((items) => (this.conversations = items));
  }

  private focusChatInput(): void {
    setTimeout(() => this.conversation?.focus(), 300);
  }

  private discardCurrent(): void {
    this.launcherSubscription?.unsubscribe();
    this.launcherSubscription = null;
    this.creatingSession = false;
    this.creationFailed = false;
    this.pendingMessage = null;
    this.loadingHistory = false;
    this.historySubscription?.unsubscribe();
    this.historySubscription = null;
  }

  private resetPanel(): void {
    this.discardCurrent();
    this.chatPanelService.close();
    this.selectSession(null);
    this.closeHistory();
  }
}
